import { useState, useEffect, useMemo } from 'react';
import { usePersistentCallback } from './hooks';
/** The rate at which the timer is refreshed in milliseconds */
const TIMER_REFRESH_RATE = 500;
/**
 * A hook that provides a countdown timer with start and stop controls
 * @param initialTime - The initial time in seconds
 * @param autoStart - Whether to start the timer automatically, default is false
 * @returns {timeLeft: number in seconds, startTimer: () => void, stopTimer: () => void, formattedTimeLeft: string in clock-style format (mm:ss or ss) }
 * @example for formattedTimeLeft
 * formattedTimeLeft(110) => '01:50 mins'
 * formattedTimeLeft(10) => '10 secs'
 */
export const useTimer = (initialTime, autoStart = false) => {
    // -1 means the timer has not started yet
    const [timeLeft, setTimeLeft] = useState(-1);
    // Used to force restart timer whenever startTimer is triggered
    const [startId, setStartId] = useState(0);
    const isTimerRunning = timeLeft > 0;
    const startTimer = usePersistentCallback(() => {
        setTimeLeft(initialTime);
        // Force restart of the timer
        setStartId((id) => id + 1);
    });
    const stopTimer = usePersistentCallback(() => {
        setTimeLeft(-1);
    });
    useEffect(() => {
        if (!isTimerRunning || startId === 0) {
            return;
        }
        const startTime = Date.now();
        const intervalId = setInterval(() => {
            const elapsedTime = Math.floor((Date.now() - startTime) / 1000);
            const newTimeLeft = Math.max(0, initialTime - elapsedTime);
            setTimeLeft(newTimeLeft);
        }, TIMER_REFRESH_RATE);
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [initialTime, isTimerRunning, startId]);
    useEffect(() => {
        // Start the timer automatically if autoStart is true and the initialTime is greater than 0
        if (autoStart && initialTime > 0) {
            startTimer();
        }
    }, [autoStart, startTimer, initialTime]);
    const inSeconds = initialTime < 60;
    const formattedTimeLeft = toFormattedClockTime(timeLeft, inSeconds);
    return useMemo(() => ({ timeLeft, formattedTimeLeft, startTimer, stopTimer }), [timeLeft, formattedTimeLeft, startTimer, stopTimer]);
};
/**
 * Formats the time in a clock-style format
 * @param time - The time in seconds
 * @param inSeconds - Whether to format the time in seconds, default is false
 * @returns The formatted time in clock-style format (mm:ss or ss)
 * @example
 * toFormattedClockTime(110) => '01:50 mins'
 * toFormattedClockTime(110, true) => '110 secs'
 */
export const toFormattedClockTime = (time, inSeconds = false) => {
    if (time === 0) {
        if (inSeconds) {
            return '00 secs';
        }
        return '00:00 mins';
    }
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');
    const formattedTime = inSeconds ? ss : `${mm}:${ss}`;
    let suffix = 'mins';
    if (minutes > 0) {
        const isExactlyOneMinute = minutes === 1 && seconds === 0;
        suffix = isExactlyOneMinute ? 'min' : 'mins';
    }
    else {
        suffix = 'secs';
    }
    return `${formattedTime} ${suffix}`;
};
